import React, { memo, useCallback, useEffect, useState } from "react"
import { alternarTema, assinarTema, estaEscuro, possuiPreferenciaTemaSalva } from "app/tema"
import clsx from "clsx"

export type BotaoAlternarTemaProps = {
    /** Mostra o texto ao lado do icone. Usar em telas com espaco (ex: login). */
    comRotulo?: boolean
    id?: string
    className?: string
    /**
     * Destaca o botao com uma borda de cor em movimento enquanto o usuario NUNCA
     * escolheu um tema, ou seja, enquanto vale o tema do sistema operacional e
     * nada foi gravado em localStorage. Serve para o botao ser notado por quem
     * ainda nao sabe que ele existe.
     *
     * O destaque dura DURACAO_DESTAQUE_MS e depois some por completo; tambem
     * termina no primeiro clique, e nunca volta.
     */
    destacarQuandoSemPreferencia?: boolean
}

/**
 * Quanto tempo o destaque fica na tela antes de sumir sozinho.
 *
 * PRECISA casar com o fade final da borda em Content/tema.css
 * (`mu-sumir-borda-botao-tema`, delay 8.8s + 1.2s de duracao = 10s): o CSS faz o
 * desaparecimento ser suave e este timer tira a classe no fim, para o motor de
 * animacao nao seguir girando de graca uma borda ja invisivel.
 */
const DURACAO_DESTAQUE_MS = 10000

/**
 * Alterna o tema claro/escuro.
 *
 * Componente autocontido de proposito: o tema e uma preferencia global
 * (localStorage + atributo no <html>, ver app/tema.ts), nao um dado do
 * container. Assim ele pode ser colocado em qualquer tela — inclusive na de
 * login, que nao tem container com esse estado — sem alterar props de ninguem.
 *
 * Os estilos (.botao-tema-mu e a troca de icone sol/lua por tema) ficam em
 * Content/tema.css, compartilhados com o botao do header em Views/Comum/_Menu.cshtml.
 */
export const BotaoAlternarTema = memo((props: BotaoAlternarTemaProps) => {
    // A fonte da verdade e o atributo data-theme no <html>; este estado existe
    // apenas para o rotulo/aria acompanharem a troca.
    const [escuro, setEscuro] = useState<boolean>(estaEscuro)

    // Tema do sistema operacional em vigor por falta de escolha do usuario — ver
    // `destacarQuandoSemPreferencia`. Comeca ligado apenas quando a tela pediu o
    // destaque, para nao pagar leitura de localStorage onde ele nao e usado.
    const destacar = props.destacarQuandoSemPreferencia === true
    const [semPreferenciaSalva, setSemPreferenciaSalva] = useState<boolean>(
        () => destacar && !possuiPreferenciaTemaSalva()
    )

    // Fim do tempo de exibicao (DURACAO_DESTAQUE_MS). Separado de
    // `semPreferenciaSalva` de proposito: a falta de preferencia continua valendo
    // (o proximo carregamento da tela destaca de novo), o que acabou foi so a
    // aparicao desta vez.
    const [tempoDeDestaqueEsgotado, setTempoDeDestaqueEsgotado] = useState(false)

    // Conta o tempo a partir do momento em que o destaque comeca a aparecer, nao
    // da montagem: se ele nem esta ativo, nao ha o que expirar.
    useEffect(() => {
        if (!semPreferenciaSalva || tempoDeDestaqueEsgotado) {
            return
        }
        const temporizador = window.setTimeout(() => setTempoDeDestaqueEsgotado(true), DURACAO_DESTAQUE_MS)
        return () => window.clearTimeout(temporizador)
    }, [semPreferenciaSalva, tempoDeDestaqueEsgotado])

    // Mantem em sincronia quando a troca vem de outro ponto (header, outra aba).
    // O destaque e reavaliado (nao simplesmente desligado) porque a notificacao
    // tambem chega quando o SO muda de tema sem preferencia gravada — nesse caso
    // o usuario ainda nao escolheu nada e o pulso deve continuar. Trocar o tema
    // em outra aba, ao contrario, grava a preferencia e encerra o destaque aqui.
    useEffect(() => assinarTema(tema => {
        setEscuro(tema === "escuro")
        setSemPreferenciaSalva(destacar && !possuiPreferenciaTemaSalva())
    }), [destacar])

    const onClick = useCallback(() => {
        alternarTema()
        // Redundante com o assinarTema acima (alternarTema sempre inverte o tema,
        // logo sempre notifica), mas garante que o pulso pare no clique mesmo se
        // um dia a notificacao deixar de vir.
        setSemPreferenciaSalva(false)
    }, [])

    const titulo = escuro ? "Mudar para tema claro" : "Mudar para tema escuro"

    return (
        <button
            type="button"
            // `no-global-button` e obrigatorio: sem ela o reset global de <button>
            // em estilo.css vence por especificidade e sobrescreve
            // borda/padding/fundo (ver APRENDIZADOS #194).
            className={clsx(
                "botao-tema-mu",
                "no-global-button",
                semPreferenciaSalva && !tempoDeDestaqueEsgotado && "botao-tema-mu--destaque",
                props.className
            )}
            id={props.id ?? "BotaoAlternarTema"}
            title={titulo}
            aria-pressed={escuro}
            onClick={onClick}
        >
            <i className="material-symbols-rounded icone-tema-escuro">dark_mode</i>
            <i className="material-symbols-rounded icone-tema-claro">light_mode</i>
            {props.comRotulo && (
                <span className="botao-tema-mu__rotulo">
                    {escuro ? "Tema Claro" : "Tema Escuro"}
                </span>
            )}
        </button>
    )
})
